import { useLayoutEffect } from 'react';

/** Dragged widths are clamped to this range. */
export const SIDEBAR_MIN_WIDTH = 180;
export const SIDEBAR_MAX_WIDTH = 420;

const SIDEBAR_WIDTH_KEY = 'taytan-sidebar-width';

export const isSidebarWidth = (width: unknown): width is number =>
    typeof width === 'number' && Number.isFinite(width) && width >= SIDEBAR_MIN_WIDTH && width <= SIDEBAR_MAX_WIDTH;

/**
 * A dragged width is a personal override of `--nav-width`, set inline on <html>
 * so it outranks the company default the Appearance page writes into the
 * `:root` token sheet. Only this one variable goes inline: colours must stay in
 * the sheet, where `body[data-theme="dark"]` can still override them.
 * `null` returns to the company default.
 */
export const applySidebarWidth = (width: number | null) => {
    const style = document.documentElement.style;
    if (width === null) style.removeProperty('--nav-width');
    else style.setProperty('--nav-width', `${width}px`);
};

const cacheSidebarWidth = (width: number | null) => {
    try {
        if (width === null) localStorage.removeItem(SIDEBAR_WIDTH_KEY);
        else localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
    } catch {
        // Storage blocked: the saved preference still applies once it loads.
    }
};

// Applied once at load, before the first paint, from the last value this
// browser saw — the saved preference arrives with a request, and without this
// the sidebar would open at the default width and then jump.
try {
    const cached = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
    if (isSidebarWidth(cached)) applySidebarWidth(cached);
} catch {
    // Storage blocked: the saved preference still applies once it loads.
}

/**
 * The user's dragged sidebar width, applied to the layout. As with the display
 * size, the saved preference is the source of truth and follows the user across
 * devices; localStorage is only the fast cache for the next cold start.
 */
export const useSidebarWidth = (sidebarWidth: number | undefined, loaded: boolean) => {
    useLayoutEffect(() => {
        if (!loaded) return;
        const width = isSidebarWidth(sidebarWidth) ? sidebarWidth : null;
        applySidebarWidth(width);
        cacheSidebarWidth(width);
    }, [sidebarWidth, loaded]);
};
